import { BasicCatalogImage, BasicCategorySummary } from "common/types";
import { Optional } from "common/utils/types";

import type { IProductLineResultFact } from "client/products/types";

export enum BlockKind {
  // Usually used as primary blocks
  Heading = "HEADING",
  SingleVisual = "SINGLE_VISUAL",
  TextBanner = "TEXT_BANNER",
  HeroVisual = "HERO_VISUAL",
  MyZoetisRewards = "MY_ZOETIS_REWARDS",

  // Usually used as secondary blocks
  Stats = "STATS",
  Facts = "FACTS",
  Image = "IMAGE",
  Video = "VIDEO",
  ActionContent = "ACTION_CONTENT",
}

// Primary Blocks

export type HeadingBlock = {
  kind: BlockKind.Heading;
  width?: number;
  preheader?: string;
  header?: string;
};

export type SingleVisualBlock = {
  kind: BlockKind.SingleVisual;
  config?: {
    visual: "left" | "right";
  };
  header?: string;
  subheader?: string;
  content?: Block;
  visual?: Block;
};

export type TextBannerBlock = {
  kind: BlockKind.TextBanner;
  text: string;
};

export type HeroVisualBlock = {
  kind: BlockKind.HeroVisual;
  visual?: Block;
  title?: string;
  subtitle?: string;
  content?: Block;
};

/* Matches _get_my_zoetis_rewards_content_block */
type MyZoetisRewardsBlockProductLine = {
  id: number;
  name: string;
  brand_name: string;
  prescription_required: boolean;
  categories: BasicCategorySummary[];
  primary_category_name: string;
  description: string;
  image: BasicCatalogImage;
  facts: IProductLineResultFact[];
  is_featured: boolean;
  patient_prescription_label: Optional<string>;
};

export type MyZoetisRewardsBlock = {
  kind: BlockKind.MyZoetisRewards;
  config?: {
    visual: "left" | "right";
  };
  product_lines: MyZoetisRewardsBlockProductLine[];
  visual: Block;
};

// Secondary Blocks

export type FactsBlock = {
  kind: BlockKind.Facts;
  facts: {
    label?: string;
    description?: string;
  }[];
};

export type ImageBlock = {
  kind: BlockKind.Image;
  source: string;
  alt: string;
  rounded?: boolean;
};

export type VideoBlock = {
  kind: BlockKind.Video;
  source: string;
  shadow?: boolean;
};

export type StatsBlock = {
  kind: BlockKind.Stats;
  stats: {
    value?: string;
    description?: string;
  }[];
};

export type ActionContentBlock = {
  kind: BlockKind.ActionContent;
  header?: string;
  content?: string[];
  action?: {
    text: string;
    url: string;
  };
};

export type Block =
  | FactsBlock
  | ImageBlock
  | VideoBlock
  | StatsBlock
  | ActionContentBlock
  | HeadingBlock
  | SingleVisualBlock
  | TextBannerBlock
  | HeroVisualBlock
  | MyZoetisRewardsBlock;
